import React, { useEffect, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useFormSave } from '../hooks/useFormSave';
import { ApplicationForm } from '../types';
import {
  getOcdeAreas,
  getOcdeDisciplines,
  getOcdeSubareas,
} from '../utils/ocde.js';
import styles from './BasicInformation.module.css';
import Select from 'shared/components/UC/Select';
import Switch from 'shared/components/UC/Switch';

import { combineClasses } from '../utils/combineClasses';

const odsOptions = [
  '1. Fin de la Pobreza',
  '2. Hambre Cero',
  '3. Salud y Bienestar',
  '4. Educación de Calidad',
  '5. Igualdad de Género',
  '6. Agua Limpia y Saneamiento',
  '7. Energía Asequible y No Contaminante',
  '8. Trabajo Decente y Crecimiento Económico',
  '9. Industria, Innovación e Infraestructura',
  '10. Reducción de las Desigualdades',
  '11. Ciudades y Comunidades Sostenibles',
  '12. Producción y Consumo Responsables',
  '13. Acción por el Clima',
  '14. Vida Submarina',
  '15. Vida de Ecosistemas Terrestres',
  '16. Paz, Justicia e Instituciones Sólidas',
  '17. Alianzas para Lograr los Objetivos',
  'NO APLICA',
];

const regiones = [
  'Región de Arica y Parinacota',
  'Región de Tarapacá',
  'Región de Antofagasta',
  'Región de Atacama',
  'Región de Coquimbo',
  'Región de Valparaíso',
  'Región Metropolitana',
  "Región del Libertador General Bernardo O'Higgins",
  'Región del Maule',
  'Región de Ñuble',
  'Región del Biobío',
  'Región de La Araucanía',
  'Región de Los Ríos',
  'Región de Los Lagos',
  'Región Aysén del General Carlos Ibáñez del Campo',
  'Región de Magallanes y de la Antártica Chilena',
  'NO APLICA',
];

// Las 16 regiones reales (sin "NO APLICA"), para el toggle "Todas las regiones".
const INDIVIDUAL_REGIONS = regiones.filter((r) => r !== 'NO APLICA');

const rcerOptions = [
  'Estación Atacama UC',
  'Estación Loa UC',
  'ECIM UC',
  'CEDEL UC',
  'Senda Darwin',
  'Parque Tantauco',
  'Estación Patagonia UC',
  'CEBIMA',
  'Parque Omora',
  'NINGUNA',
];

const PARTNER_TYPES = [
  { value: 'publico', label: 'Público' },
  { value: 'privado', label: 'Privado' },
  { value: 'sociedad_civil', label: 'Sociedad civil' },
];

interface BasicInformationProps {
  readOnly?: boolean;
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  readOnly = false,
}) => {
  const {
    register,
    formState: { errors },
    watch,
    reset,
    setValue,
    control,
  } = useFormContext<ApplicationForm>();

  const { loadInitialData, setUpdating, mode } = useFormSave({
    stepId: 'basic',
  });

  const selectedOcdeArea = watch('oecdArea');
  const selectedOcdeSubarea = watch('oecdSubarea');
  const keywords = watch('keywords') || '';

  // Get OCDE options based on hierarchy
  const ocdeAreas = useMemo(() => getOcdeAreas(), []);
  const ocdeSubareas = useMemo(() => {
    return selectedOcdeArea ? getOcdeSubareas(parseInt(selectedOcdeArea)) : [];
  }, [selectedOcdeArea]);
  const ocdeDisciplines = useMemo(() => {
    return selectedOcdeSubarea
      ? getOcdeDisciplines(parseInt(selectedOcdeSubarea))
      : [];
  }, [selectedOcdeSubarea]);

  // Use useController for proper react-hook-form integration
  const {
    field: genderApproachField,
    fieldState: { error: genderApproachError },
  } = useController({
    name: 'genderApproachOptions',
    control,
    defaultValue: [],
    rules: {
      validate: (value: string[]) => {
        if (!value || value.length === 0) {
          return 'Debe seleccionar al menos una opción';
        }
        return true;
      },
    },
  });

  // Register the genderApproachProject field
  const genderApproachProjectField = useController({
    name: 'genderApproachProject',
    control,
    defaultValue: 'NO',
  });

  const genderApproachOptions = useMemo(() => {
    return genderApproachField.value || [];
  }, [genderApproachField.value]);

  // Use useController for RCER with mutual exclusion
  const {
    field: rcerField,
    fieldState: { error: rcerError },
  } = useController({
    name: 'rcer',
    control,
    defaultValue: [],
    rules: {
      validate: (value: string[]) => {
        if (!value || value.length === 0) {
          return 'Debe seleccionar al menos una opción';
        }
        return true;
      },
    },
  });

  const selectedRcer = useMemo(() => {
    return rcerField.value || [];
  }, [rcerField.value]);

  // Use useController for SDG Goals with mutual exclusion
  const {
    field: sdgGoalsField,
    fieldState: { error: sdgGoalsError },
  } = useController({
    name: 'sdgGoals',
    control,
    defaultValue: [],
    rules: {
      validate: (value: string[]) => {
        if (!value || value.length === 0) {
          return 'Debe seleccionar al menos una opción';
        }
        if (value.length > 3) {
          return 'Puede seleccionar máximo 3 opciones';
        }
        return true;
      },
    },
  });

  const selectedSdgGoals = useMemo(() => {
    return sdgGoalsField.value || [];
  }, [sdgGoalsField.value]);

  // Use useController for Execution Regions with mutual exclusion
  const {
    field: executionRegionsField,
    fieldState: { error: executionRegionsError },
  } = useController({
    name: 'executionRegions',
    control,
    defaultValue: [],
    rules: {
      validate: (value: string[]) => {
        if (!value || value.length === 0) {
          return 'Debe seleccionar al menos una región';
        }
        return true;
      },
    },
  });

  const selectedExecutionRegions = useMemo(() => {
    return executionRegionsField.value || [];
  }, [executionRegionsField.value]);

  // Use useController for Pacto Educativo Global with "NINGUNA" mutual exclusion
  const {
    field: globalEducationPactField,
    fieldState: { error: globalEducationPactError },
  } = useController({
    name: 'globalEducationPact',
    control,
    defaultValue: [],
    rules: {
      validate: (value: string[] | undefined) => {
        if (!value || value.length === 0) {
          return 'Debe seleccionar al menos una opción';
        }
        return true;
      },
    },
  });

  const selectedGlobalEducationPact = useMemo(() => {
    return globalEducationPactField.value || [];
  }, [globalEducationPactField.value]);

  const {
    field: evaluationAreaField,
    fieldState: { error: evaluationAreaError },
  } = useController({
    name: 'evaluationArea',
    control,
    defaultValue: '',
    rules: { required: 'Este campo es obligatorio' },
  });

  const {
    field: partnerTypesField,
    fieldState: { error: partnerTypesError },
  } = useController({
    name: 'externalPartnerTypes',
    control,
    defaultValue: [],
    rules: {
      validate: (v) =>
        watch('externalPartners') !== 'SI' ||
        (Array.isArray(v) && v.length > 0) ||
        'Seleccione al menos un tipo de actor',
    },
  });

  const {
    field: oecdAreaField,
    fieldState: { error: oecdAreaError },
  } = useController({
    name: 'oecdArea',
    control,
    defaultValue: '',
    rules: { required: 'Este campo es obligatorio' },
  });

  const {
    field: oecdSubareaField,
    fieldState: { error: oecdSubareaError },
  } = useController({
    name: 'oecdSubarea',
    control,
    defaultValue: '',
    rules: { required: selectedOcdeArea ? 'Este campo es obligatorio' : false },
  });

  const {
    field: oecdDisciplineField,
  } = useController({
    name: 'oecdDiscipline',
    control,
    defaultValue: '',
  });

  // Handle gender approach option selection with mutual exclusion
  const handleGenderOptionChange = (
    optionValue: string,
    isChecked: boolean
  ) => {
    if (readOnly) return; // Don't allow changes in read-only mode

    let newOptions = Array.isArray(genderApproachOptions)
      ? [...genderApproachOptions]
      : [];

    if (isChecked) {
      // Add the selected option
      newOptions.push(optionValue);

      // Apply exclusion rules
      if (['1', '2', '3'].includes(optionValue)) {
        // If selecting 1, 2, or 3, remove 4 and 5
        newOptions = newOptions.filter((opt) => !['4', '5'].includes(opt));
      } else if (optionValue === '4') {
        // If selecting 4, remove 1, 2, 3, and 5
        newOptions = newOptions.filter(
          (opt) => !['1', '2', '3', '5'].includes(opt)
        );
      } else if (optionValue === '5') {
        // If selecting 5, remove all others (1, 2, 3, 4)
        newOptions = newOptions.filter(
          (opt) => !['1', '2', '3', '4'].includes(opt)
        );
      }
    } else {
      // Remove the option
      newOptions = newOptions.filter((opt) => opt !== optionValue);
    }

    genderApproachField.onChange(newOptions);
    genderApproachField.onBlur();

    // Immediately update genderApproachProject based on the new options
    const hasPositiveOptions = newOptions.some((option: string) =>
      ['1', '2', '3'].includes(option)
    );
    const result = hasPositiveOptions ? 'SI' : 'NO';
    genderApproachProjectField.field.onChange(result);
    genderApproachProjectField.field.onBlur();
  };

  // Handle RCER option selection: "NINGUNA" is mutually exclusive with the rest
  const handleRcerChange = (optionValue: string, isChecked: boolean) => {
    if (readOnly) return;

    // Normalize stale lowercase 'Ninguna' values that may exist in localStorage
    let newOptions = (Array.isArray(selectedRcer) ? [...selectedRcer] : []).filter(
      (o) => rcerOptions.includes(o)
    );

    if (optionValue === 'NINGUNA') {
      newOptions = isChecked ? ['NINGUNA'] : [];
    } else {
      if (isChecked) {
        newOptions = newOptions
          .filter((o) => o !== 'NINGUNA' && o !== optionValue)
          .concat(optionValue);
      } else {
        newOptions = newOptions.filter((opt) => opt !== optionValue);
      }
    }

    rcerField.onChange(newOptions);
    rcerField.onBlur();
  };

  // Handle SDG Goals option selection (no auto-deselection, just disable conflicts)
  const handleSdgGoalsChange = (optionValue: string, isChecked: boolean) => {
    if (readOnly) return;

    let newOptions = Array.isArray(selectedSdgGoals)
      ? [...selectedSdgGoals]
      : [];

    if (isChecked) {
      newOptions.push(optionValue);
    } else {
      newOptions = newOptions.filter((opt) => opt !== optionValue);
    }

    sdgGoalsField.onChange(newOptions);
    sdgGoalsField.onBlur();
  };

  const selectedPartnerTypes: string[] = partnerTypesField.value || [];

  // Nombres de socios por tipo de actor (obs. DINV: permitir más de uno).
  // Modelo: externalPartnerNames.{tipo} = string[]. Se maneja manualmente (no
  // register) por ser una lista dinámica anidada por tipo.
  const getPartnerNames = (type: string): string[] => {
    const v = (watch('externalPartnerNames') as any)?.[type];
    return Array.isArray(v) ? v : [];
  };

  const setPartnerNames = (type: string, names: string[]) => {
    setValue(`externalPartnerNames.${type}` as any, names, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const addPartnerName = (type: string) => {
    if (readOnly) return;
    setPartnerNames(type, [...getPartnerNames(type), '']);
  };

  const updatePartnerName = (type: string, index: number, value: string) => {
    const names = [...getPartnerNames(type)];
    names[index] = value;
    setPartnerNames(type, names);
  };

  const removePartnerName = (type: string, index: number) => {
    if (readOnly) return;
    setPartnerNames(
      type,
      getPartnerNames(type).filter((_, i) => i !== index)
    );
  };

  const togglePartnerType = (value: string, isChecked: boolean) => {
    if (readOnly) return;
    const cur = Array.isArray(selectedPartnerTypes)
      ? [...selectedPartnerTypes]
      : [];
    const next = isChecked ? [...cur, value] : cur.filter((v) => v !== value);
    partnerTypesField.onChange(next);
    partnerTypesField.onBlur();
    // Al activar un tipo, sembrar un input vacío; al desactivarlo, limpiar.
    if (isChecked) {
      if (getPartnerNames(value).length === 0) setPartnerNames(value, ['']);
    } else {
      setPartnerNames(value, []);
    }
  };

  const handleExecutionRegionsChange = (
    optionValue: string,
    isChecked: boolean
  ) => {
    if (readOnly) return;

    let newOptions = Array.isArray(selectedExecutionRegions)
      ? [...selectedExecutionRegions]
      : [];

    if (optionValue === 'NO APLICA') {
      newOptions = isChecked ? ['NO APLICA'] : newOptions.filter((o) => o !== 'NO APLICA');
    } else if (isChecked) {
      newOptions = [...newOptions.filter((o) => o !== optionValue), optionValue];
    } else {
      newOptions = newOptions.filter((o) => o !== optionValue);
    }

    executionRegionsField.onChange(newOptions);
    executionRegionsField.onBlur();
  };

  // Toggle "Todas las regiones": control puro, no persiste ningún literal.
  // Encender selecciona las 16 regiones reales; apagar limpia la selección.
  const allRegionsSelected =
    INDIVIDUAL_REGIONS.length > 0 &&
    INDIVIDUAL_REGIONS.every((r) => selectedExecutionRegions.includes(r));

  const toggleAllRegions = (checked: boolean) => {
    if (readOnly) return;
    executionRegionsField.onChange(checked ? [...INDIVIDUAL_REGIONS] : []);
    executionRegionsField.onBlur();
  };

  // Handle Pacto Educativo Global: "NINGUNA" is mutually exclusive with the 5 areas
  const handleGlobalEducationPactChange = (
    optionValue: string,
    isChecked: boolean
  ) => {
    if (readOnly) return;
    let newOptions = Array.isArray(selectedGlobalEducationPact)
      ? [...selectedGlobalEducationPact]
      : [];
    if (optionValue === 'NINGUNA') {
      newOptions = isChecked ? ['NINGUNA'] : [];
    } else {
      if (isChecked) {
        newOptions = newOptions
          .filter((o) => o !== 'NINGUNA' && o !== optionValue)
          .concat(optionValue);
      } else {
        newOptions = newOptions.filter((o) => o !== optionValue);
      }
    }
    globalEducationPactField.onChange(newOptions);
    globalEducationPactField.onBlur();
  };

  // Handle keywords as 3 separate inputs
  const keywordsList = keywords ? keywords.split(';') : ['', '', ''];
  const handleKeywordChange = (index: number, value: string) => {
    if (readOnly) return; // Don't allow changes in read-only mode
    const updatedKeywords = [...keywordsList];
    updatedKeywords[index] = value;
    // Don't trim during typing - just join non-empty values
    // Trimming will happen on save/submit
    const keywordsString = updatedKeywords.filter((k) => k !== '').join(';');
    setValue('keywords', keywordsString, { shouldValidate: true });
  };

  // Trim keyword when input loses focus
  const handleKeywordBlur = (index: number) => {
    if (readOnly) return;
    const trimmedValue = keywordsList[index]?.trim() || '';
    if (trimmedValue !== keywordsList[index]) {
      handleKeywordChange(index, trimmedValue);
    }
  };

  // Load initial data when component mounts
  useEffect(() => {
    if (mode === 'static') {
      const initialData = loadInitialData();
      if (Object.keys(initialData).length > 0) {
        // Convert existing genderApproachProject to genderApproachOptions if not present
        // This handles migration from old data that only had SI/NO
        if (
          initialData.genderApproachProject &&
          !initialData.genderApproachOptions
        ) {
          if (initialData.genderApproachProject === 'SI') {
            // Default to option 1 if we only know it was "SI"
            initialData.genderApproachOptions = ['1'];
          } else {
            // Default to option 4 if it was "NO"
            initialData.genderApproachOptions = ['4'];
          }
        }
        // Ensure genderApproachOptions is always an array
        if (!Array.isArray(initialData.genderApproachOptions)) {
          initialData.genderApproachOptions = [];
        }
        setUpdating(true);
        reset(initialData);
        setTimeout(() => setUpdating(false), 100);
      }
    }
  }, [loadInitialData, reset, setUpdating, mode]);

  return (
    <div>
      <h2 className={styles.sectionTitle}>Antecedentes del Proyecto</h2>
      <div className={styles.sectionInfoBox}>
        Esta sección recopila información sobre el proyecto. Cada campo está
        marcado con un asterisco (*), lo que indica que es obligatorio.
      </div>

      <section className={styles.section}>
        {/* Título del Proyecto */}
        <div className={styles.formGroup}>
          <label htmlFor="projectTitle" className={styles.label}>
            Título del Proyecto *
          </label>
          <div className={styles.fieldDescription}>
            Ingresar con mayúsculas y sin tildes.
          </div>
          <textarea
            id="projectTitle"
            className={combineClasses(
              styles.textarea,
              errors.projectTitle && styles.error,
              readOnly && styles.readOnlyField
            )}
            rows={3}
            {...register('projectTitle', {
              required: 'Este campo es obligatorio',
            })}
            readOnly={readOnly}
          />
          {errors.projectTitle && (
            <span className={styles.errorMessage}>
              {errors.projectTitle.message}
            </span>
          )}
        </div>

        {/* ORCID del Investigador Responsable (obs. DINV) */}
        <div className={styles.formGroup}>
          <label htmlFor="orcid" className={styles.label}>
            Número de ORCID
          </label>
          <div className={styles.fieldDescription}>
            Registra tu número de ORCID (formato 0000-0000-0000-0000).
          </div>
          <input
            id="orcid"
            type="text"
            className={combineClasses(
              styles.input,
              styles.orcidInput,
              errors.orcid && styles.error,
              readOnly && styles.readOnlyField
            )}
            {...register('orcid', {
              pattern: {
                value: /^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/,
                message: 'Formato ORCID inválido (0000-0000-0000-0000).',
              },
            })}
            placeholder="0000-0000-0000-0000"
            maxLength={19}
            readOnly={readOnly}
          />
          {errors.orcid && (
            <span className={styles.errorMessage}>{errors.orcid.message}</span>
          )}
        </div>

        {/* Proyecto Interdisciplinario */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Proyecto Interdisciplinario *</label>
          <div className={styles.fieldDescription}>
            Se define como aquel proyecto cuya pregunta de investigación aborda
            un problema desde diversas disciplinas y metodologías, integrando
            enfoques complementarios.
          </div>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="SI"
                className={styles.radioInput}
                {...register('interdisciplinaryProject', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              SI
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="NO"
                className={styles.radioInput}
                {...register('interdisciplinaryProject', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              NO
            </label>
          </div>
          {errors.interdisciplinaryProject && (
            <span className={styles.errorMessage}>
              {errors.interdisciplinaryProject.message}
            </span>
          )}
        </div>

        {/* Proyecto Transdisciplinario */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Proyecto Transdisciplinario *</label>
          <div className={styles.fieldDescription}>
            Un proyecto transdisciplinario enfatiza la incorporación de actores
            extraacadémicos en procesos de co-construcción del conocimiento para
            la resolución de problemas complejos, tanto globales como locales,
            sobre la base de la colaboración y la pluralidad epistemológica.
          </div>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="SI"
                className={styles.radioInput}
                {...register('transdisciplinaryProject', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              SI
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="NO"
                className={styles.radioInput}
                {...register('transdisciplinaryProject', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              NO
            </label>
          </div>
          {errors.transdisciplinaryProject && (
            <span className={styles.errorMessage}>
              {errors.transdisciplinaryProject.message}
            </span>
          )}
        </div>

        {/* Proyecto con Enfoque de Género */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Proyecto con Enfoque de Género *
          </label>
          <div className={styles.fieldDescription}>
            Seleccione todas las opciones que apliquen. Si selecciona "No sabe"
            no puede escoger otras opciones.
          </div>
          <div className={styles.checkboxGroupColumn}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                value="1"
                className={styles.checkboxInput}
                checked={
                  Array.isArray(genderApproachOptions) &&
                  genderApproachOptions.includes('1')
                }
                onChange={(e) =>
                  handleGenderOptionChange('1', e.target.checked)
                }
                disabled={readOnly}
              />
              1. Incorpora el enfoque de género de forma explícita en la
              problematización, revisión de antecedentes, en la pregunta de
              investigación, hipótesis o análisis central
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                value="2"
                className={styles.checkboxInput}
                checked={
                  Array.isArray(genderApproachOptions) &&
                  genderApproachOptions.includes('2')
                }
                onChange={(e) =>
                  handleGenderOptionChange('2', e.target.checked)
                }
                disabled={readOnly}
              />
              2. Incorpora la variable sexo y/o género en el análisis de los
              resultados y/o en las conclusiones
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                value="3"
                className={styles.checkboxInput}
                checked={
                  Array.isArray(genderApproachOptions) &&
                  genderApproachOptions.includes('3')
                }
                onChange={(e) =>
                  handleGenderOptionChange('3', e.target.checked)
                }
                disabled={readOnly}
              />
              3. Incorpora la variable género en el proceso de divulgación de
              los resultados y en las estrategias de comunicación y
              transferencia
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                value="4"
                className={styles.checkboxInput}
                checked={
                  Array.isArray(genderApproachOptions) &&
                  genderApproachOptions.includes('4')
                }
                onChange={(e) =>
                  handleGenderOptionChange('4', e.target.checked)
                }
                disabled={readOnly}
              />
              4. El proyecto no incorpora el enfoque de género en ninguna
              modalidad
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                value="5"
                className={styles.checkboxInput}
                checked={
                  Array.isArray(genderApproachOptions) &&
                  genderApproachOptions.includes('5')
                }
                onChange={(e) =>
                  handleGenderOptionChange('5', e.target.checked)
                }
                disabled={readOnly}
              />
              5. No sabe
            </label>
          </div>
          {genderApproachError && (
            <span className={styles.errorMessage}>
              {genderApproachError.message}
            </span>
          )}
        </div>

        {/* Ciencia Abierta */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Ciencia Abierta *</label>
          <div className={styles.fieldDescription}>
            Proyectos que buscan publicar sus resultados y datos de
            investigación a través de rutas de acceso abierto, ya sea mediante
            pago de cargos de publicación, depósito en repositorios
            institucionales o publicación en revistas de acceso abierto.
          </div>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="SI"
                className={styles.radioInput}
                {...register('openScience', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              SI
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="NO"
                className={styles.radioInput}
                {...register('openScience', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              NO
            </label>
          </div>
          {errors.openScience && (
            <span className={styles.errorMessage}>
              {errors.openScience.message}
            </span>
          )}
        </div>

        {/* RCER */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Red de Centros y Estaciones Regionales asociadas (RCER) *
          </label>
          <div className={styles.fieldDescription}>
            La postulación considera actividades de investigación, docencia y/o
            vinculación en alguno(s) de los Centros y Estaciones Regionales de
            la RCER. Puede seleccionar múltiples opciones.
          </div>
          <div className={styles.checkboxGroupColumn}>
            {rcerOptions.map((rcer) => (
              <label key={rcer} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  value={rcer}
                  className={styles.checkboxInput}
                  checked={
                    Array.isArray(selectedRcer) && selectedRcer.includes(rcer)
                  }
                  onChange={(e) => handleRcerChange(rcer, e.target.checked)}
                  disabled={readOnly}
                />
                {rcer}
              </label>
            ))}
          </div>
          {rcerError && (
            <span className={styles.errorMessage}>{rcerError.message}</span>
          )}
        </div>

        {/* Palabras Clave */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Palabras Clave / Keywords *</label>
          <div className={styles.fieldDescription}>
            Ingresar con mayúsculas, sin tildes. Máximo 3 palabras clave.
          </div>
          <div className={styles.keywordsContainer}>
            <input
              type="text"
              placeholder="Palabra clave 1"
              className={combineClasses(
                styles.input,
                styles.keywordInput,
                errors.keywords && styles.error,
                readOnly && styles.readOnlyField
              )}
              value={keywordsList[0] || ''}
              onChange={(e) => handleKeywordChange(0, e.target.value)}
              onBlur={() => handleKeywordBlur(0)}
              readOnly={readOnly}
            />
            <input
              type="text"
              placeholder="Palabra clave 2"
              className={combineClasses(
                styles.input,
                styles.keywordInput,
                errors.keywords && styles.error,
                readOnly && styles.readOnlyField
              )}
              value={keywordsList[1] || ''}
              onChange={(e) => handleKeywordChange(1, e.target.value)}
              onBlur={() => handleKeywordBlur(1)}
              readOnly={readOnly}
            />
            <input
              type="text"
              placeholder="Palabra clave 3"
              className={combineClasses(
                styles.input,
                styles.keywordInput,
                errors.keywords && styles.error,
                readOnly && styles.readOnlyField
              )}
              value={keywordsList[2] || ''}
              onChange={(e) => handleKeywordChange(2, e.target.value)}
              onBlur={() => handleKeywordBlur(2)}
              readOnly={readOnly}
            />
          </div>
          <input
            type="hidden"
            {...register('keywords', {
              required: 'Debe ingresar al menos una palabra clave',
              validate: (value) => {
                const keywords = value
                  ? value.split(';').filter((k) => k.trim())
                  : [];
                if (keywords.length === 0) {
                  return 'Debe ingresar al menos una palabra clave';
                }
                return true;
              },
            })}
          />
          {errors.keywords && (
            <span className={styles.errorMessage}>
              {errors.keywords.message}
            </span>
          )}
        </div>

        {/* Clasificación OCDE */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Clasificación OCDE *</label>
          <div className={styles.fieldDescription}>
            Seleccione la clasificación OCDE de su proyecto. La disciplina es
            opcional.
          </div>

          <div className={styles.selectWidthM}>
            <label htmlFor="oecdArea" className={styles.subLabel}>
              Área OCDE *
            </label>
            <Select
              id="oecdArea"
              className={styles.selectSpaced}
              label=""
              placeholder="Seleccione un área"
              options={ocdeAreas.map((area) => ({
                value: area.id.toString(),
                label: area.name || '',
              }))}
              value={oecdAreaField.value || ''}
              onChange={(e) => { oecdAreaField.onChange(e); oecdAreaField.onBlur(); }}
              error={oecdAreaError?.message}
              disabled={readOnly}
            />
          </div>

          <div className={styles.selectWidthM}>
            <label htmlFor="oecdSubarea" className={styles.subLabel}>
              Subárea OCDE *
            </label>
            <Select
              id="oecdSubarea"
              className={styles.selectSpaced}
              label=""
              placeholder={
                selectedOcdeArea
                  ? 'Seleccione una subárea'
                  : 'Primero seleccione un área OCDE'
              }
              options={ocdeSubareas.map((subarea) => ({
                value: subarea.id.toString(),
                label: subarea.name || '',
              }))}
              value={oecdSubareaField.value || ''}
              onChange={(e) => { oecdSubareaField.onChange(e); oecdSubareaField.onBlur(); }}
              error={oecdSubareaError?.message}
              disabled={readOnly || !selectedOcdeArea}
            />
          </div>

          <div className={styles.selectWidthM}>
            <label htmlFor="oecdDiscipline" className={styles.subLabel}>
              Disciplina OCDE (opcional)
            </label>
            <Select
              id="oecdDiscipline"
              label=""
              placeholder={
                !selectedOcdeSubarea
                  ? 'Primero seleccione una subárea OCDE'
                  : ocdeDisciplines.length === 0
                    ? 'No hay disciplinas disponibles para esta subárea'
                    : 'Seleccione una disciplina (opcional)'
              }
              options={ocdeDisciplines.map((discipline) => ({
                value: discipline.id.toString(),
                label: discipline.name || '',
              }))}
              value={oecdDisciplineField.value || ''}
              onChange={(e) => { oecdDisciplineField.onChange(e); oecdDisciplineField.onBlur(); }}
              disabled={
                readOnly || !selectedOcdeSubarea || ocdeDisciplines.length === 0
              }
            />
          </div>
        </div>

        {/* Línea de investigación de la propuesta (reemplaza al antiguo
            "Área de Evaluación"; NO es la clasificación OCDE). Texto libre. */}
        <div className={styles.formGroup}>
          <label htmlFor="evaluationArea" className={styles.label}>
            Línea de investigación de la propuesta *
          </label>
          <div className={styles.fieldDescription}>
            Indique la línea de investigación en que se enmarca la propuesta.
          </div>
          <input
            id="evaluationArea"
            type="text"
            className={combineClasses(
              styles.input,
              evaluationAreaError && styles.error,
              readOnly && styles.readOnlyField
            )}
            placeholder="Ej.: Ecología de ecosistemas costeros"
            value={evaluationAreaField.value || ''}
            onChange={evaluationAreaField.onChange}
            onBlur={evaluationAreaField.onBlur}
            readOnly={readOnly}
            maxLength={200}
          />
          {evaluationAreaError && (
            <span className={styles.errorMessage}>
              {evaluationAreaError.message}
            </span>
          )}
        </div>

        {/* ODS */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Objetivos de Desarrollo Sostenible (ODS) *
          </label>
          <div className={styles.fieldDescription}>
            Seleccionar un máximo de 3.
          </div>
          <div className={styles.checkboxGroupColumn}>
            {odsOptions.map((ods) => {
              const isNoAplica = ods === 'NO APLICA';
              const hasNoAplicaSelected =
                selectedSdgGoals.includes('NO APLICA');
              const hasOthersSelected = selectedSdgGoals.some(
                (opt) => opt !== 'NO APLICA'
              );
              const isChecked =
                Array.isArray(selectedSdgGoals) &&
                selectedSdgGoals.includes(ods);
              // Tope de 3 (obs. DINV): al llegar a 3 ODS seleccionados,
              // deshabilitar los no marcados en vez de solo validar al enviar.
              const maxReached =
                selectedSdgGoals.filter((o) => o !== 'NO APLICA').length >= 3;
              const isDisabled =
                readOnly ||
                (isNoAplica && hasOthersSelected) ||
                (!isNoAplica && hasNoAplicaSelected) ||
                (!isNoAplica && !isChecked && maxReached);

              return (
                <label key={ods} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    value={ods}
                    className={styles.checkboxInput}
                    checked={isChecked}
                    onChange={(e) =>
                      handleSdgGoalsChange(ods, e.target.checked)
                    }
                    disabled={isDisabled}
                  />
                  {ods}
                </label>
              );
            })}
          </div>
          {sdgGoalsError && (
            <span className={styles.errorMessage}>{sdgGoalsError.message}</span>
          )}
        </div>

        {/* Región de Ejecución */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Región de Ejecución *</label>
          {/* Toggle que selecciona/deselecciona las 16 regiones de una vez.
              Reemplaza al viejo checkbox "TODAS LAS REGIONES (16)" que se
              persistía como dato (bug DINV). */}
          <Switch
            id="toggle-todas-las-regiones"
            label="Todas las regiones (16)"
            checked={allRegionsSelected}
            onChange={(e) => toggleAllRegions(e.target.checked)}
            disabled={readOnly || selectedExecutionRegions.includes('NO APLICA')}
            className={styles.regionsToggle}
          />
          <div className={styles.checkboxGroupColumn}>
            {regiones.map((region) => {
              const hasNoAplica = selectedExecutionRegions.includes('NO APLICA');
              const hasOthers = selectedExecutionRegions.some((r) => r !== 'NO APLICA');
              const isDisabled =
                region === 'NO APLICA'
                  ? readOnly || hasOthers
                  : readOnly || hasNoAplica;

              return (
                <label key={region} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    value={region}
                    className={styles.checkboxInput}
                    checked={
                      Array.isArray(selectedExecutionRegions) &&
                      selectedExecutionRegions.includes(region)
                    }
                    onChange={(e) =>
                      handleExecutionRegionsChange(region, e.target.checked)
                    }
                    disabled={isDisabled}
                  />
                  {region}
                </label>
              );
            })}
          </div>
          {executionRegionsError && (
            <span className={styles.errorMessage}>
              {executionRegionsError.message}
            </span>
          )}
        </div>
        {/* Vinculación con el medio (socios externos nacionales) */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Vinculación con el medio *
            <span
              className={styles.helpTip}
              tabIndex={0}
              aria-label="Más información sobre actores externos"
            >
              <i className="uc-icon icon-size--sm">info</i>
              <span className={styles.helpTipBox} role="tooltip">
                Se entiende por actores externos no universitarios ni de
                investigación a los siguientes actores:
                <br />
                <strong>Público:</strong> municipalidades, servicios públicos,
                ministerios, gobiernos regionales, etc.
                <br />
                <strong>Privado:</strong> empresas, consultoras, emprendimientos,
                etc.
                <br />
                <strong>Sociedad civil:</strong> fundaciones, ONG, organizaciones
                comunitarias, gremios, etc.
              </span>
            </span>
          </label>
          <div className={styles.fieldDescription}>
            ¿Su proyecto considera la participación de uno o más socios externos
            nacionales distintos de universidades y centros de investigación?
          </div>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="SI"
                className={styles.radioInput}
                {...register('externalPartners', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              Sí
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="NO"
                className={styles.radioInput}
                {...register('externalPartners', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              No
            </label>
          </div>
          {errors.externalPartners && (
            <span className={styles.errorMessage}>
              {errors.externalPartners.message}
            </span>
          )}

          {watch('externalPartners') === 'SI' && (
            <div className={styles.conditionalField}>
              <label className={styles.subLabel}>
                Señale el/los tipo(s) de actor(es) externo(s) nacional(es)
                distinto(s) de universidades y centros de investigación
                involucrados en su propuesta: *
              </label>
              <div className={styles.checkboxGroupColumn}>
                {PARTNER_TYPES.map((t) => (
                  <label key={t.value} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      value={t.value}
                      className={styles.checkboxInput}
                      checked={selectedPartnerTypes.includes(t.value)}
                      onChange={(e) =>
                        togglePartnerType(t.value, e.target.checked)
                      }
                      disabled={readOnly}
                    />
                    {t.label}
                  </label>
                ))}
              </div>
              {partnerTypesError && (
                <span className={styles.errorMessage}>
                  {partnerTypesError.message}
                </span>
              )}

              {PARTNER_TYPES.filter((t) =>
                selectedPartnerTypes.includes(t.value)
              ).map((t) => {
                // Obs. DINV: un input por socio, con "+ Agregar socio".
                const names = getPartnerNames(t.value);
                const rows = names.length > 0 ? names : [''];
                return (
                  <div key={t.value} className={styles.conditionalField}>
                    <label className={styles.subLabel}>
                      Indique nombre(s) de socio(s) ({t.label}): *
                    </label>
                    <div className={styles.fieldDescription}>
                      Agregue un socio por campo; use "+ Agregar socio" para
                      sumar más.
                    </div>
                    {rows.map((name, index) => (
                      <div key={index} className={styles.partnerNameRow}>
                        <input
                          type="text"
                          className={combineClasses(
                            styles.input,
                            readOnly && styles.readOnlyField
                          )}
                          value={name}
                          onChange={(e) =>
                            updatePartnerName(t.value, index, e.target.value)
                          }
                          placeholder={`Nombre de socio ${t.label.toLowerCase()}`}
                          maxLength={300}
                          readOnly={readOnly}
                        />
                        {!readOnly && rows.length > 1 && (
                          <button
                            type="button"
                            className={styles.partnerNameRemove}
                            onClick={() => removePartnerName(t.value, index)}
                            aria-label="Quitar socio"
                          >
                            <i className="uc-icon icon-size--sm">delete</i>
                          </button>
                        )}
                      </div>
                    ))}
                    {!readOnly && (
                      <button
                        type="button"
                        className={styles.partnerNameAdd}
                        onClick={() => addPartnerName(t.value)}
                      >
                        + Agregar socio
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pacto Educativo Global */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Pacto Educativo Global *</label>
          <div className={styles.fieldDescription}>
            Indique si la propuesta tributa a una de las 5 áreas específicas de
            investigación del Pacto Educativo Global.
          </div>
          <div className={styles.checkboxGroupColumn}>
            {[
              {
                value: 'dignidad',
                label:
                  'Dignidad de la Persona y Derechos Humanos: Centrada en fundamentar el valor intrínseco de cada ser humano en los procesos formativos.',
              },
              {
                value: 'fraternidad',
                label:
                  'Fraternidad y Cooperación: Orientada a superar divisiones mediante la solidaridad y el desarrollo social conjunto.',
              },
              {
                value: 'tecnologia',
                label:
                  'Tecnología y Ecología Integral: Enfocada en estudiar el cuidado de la casa común, la sostenibilidad y la ética en la innovación tecnológica.',
              },
              {
                value: 'paz',
                label:
                  'Educar para la Paz y la Ciudadanía: Destinada a formar ciudadanos responsables que promuevan activamente la justicia, la convivencia y la paz.',
              },
              {
                value: 'culturas',
                label:
                  'Culturas y Religiones: Basada en la promoción del diálogo intercultural e interreligioso para generar un mutuo entendimiento social.',
              },
            ].map(({ value, label }) => (
              <label key={value} className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  value={value}
                  className={styles.checkboxInput}
                  checked={selectedGlobalEducationPact.includes(value)}
                  onChange={(e) =>
                    handleGlobalEducationPactChange(value, e.target.checked)
                  }
                  disabled={readOnly || selectedGlobalEducationPact.includes('NINGUNA')}
                />
                {label}
              </label>
            ))}
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                value="NINGUNA"
                className={styles.checkboxInput}
                checked={selectedGlobalEducationPact.includes('NINGUNA')}
                onChange={(e) =>
                  handleGlobalEducationPactChange('NINGUNA', e.target.checked)
                }
                disabled={readOnly}
              />
              No tributa a ninguna de las áreas del Pacto Educativo Global
            </label>
          </div>
          {globalEducationPactError && (
            <span className={styles.errorMessage}>
              {globalEducationPactError.message}
            </span>
          )}
        </div>

        {/* Uso de Inteligencia Artificial */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Uso de Inteligencia Artificial *
          </label>
          <div className={styles.fieldDescription}>
            Indique si ha utilizado Inteligencia Artificial para escribir la
            propuesta.
          </div>
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="SI"
                className={styles.radioInput}
                {...register('usedAI', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              SI
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                value="NO"
                className={styles.radioInput}
                {...register('usedAI', {
                  required: 'Este campo es obligatorio',
                })}
                disabled={readOnly}
              />
              NO
            </label>
          </div>
          {watch('usedAI') === 'SI' && (
            <div className={styles.conditionalField}>
              <label className={styles.subLabel}>
                Especifique cuál herramienta, versión, prompt utilizado y en qué
                parte específica de la propuesta *
              </label>
              <textarea
                className={combineClasses(
                  styles.textarea,
                  errors.aiDetails && styles.error,
                  readOnly && styles.readOnlyField
                )}
                rows={4}
                {...register('aiDetails', {
                  required: 'Debe especificar el uso de IA',
                })}
                readOnly={readOnly}
                placeholder="Ej: ChatGPT (GPT-4o), versión mayo 2025. Prompt: «Resume en tres párrafos...». Utilizado en la redacción del resumen ejecutivo."
              />
              {errors.aiDetails && (
                <span className={styles.errorMessage}>
                  {errors.aiDetails.message}
                </span>
              )}
            </div>
          )}
          {errors.usedAI && (
            <span className={styles.errorMessage}>{errors.usedAI.message}</span>
          )}
        </div>
      </section>
    </div>
  );
};

export default BasicInformation;
